// src/pages/BecomeOrganizer.js
//
// Lets an already-logged-in participant request organizer status, without
// needing to log out and go through the login-screen-only self-signup
// flow (which also requires NOT already having an account -- a current
// participant literally couldn't use it before this).
//
// The request creates a pending_review organizer row -- NOT instantly
// active. A platform admin must approve it via the existing Organizers
// dashboard before it has any actual effect (see the backend's
// _resolveOrganizer fix: pending_review has no more access than a plain
// participant).

import React, { useState } from 'react';
import { requestOrganizer, createCheckoutSession } from '../services/api';

function BecomeOrganizer({ user = {}, onRequested }) {
    const [submitting, setSubmitting] = useState(false);
    const [error,      setError]      = useState('');
    const [checkingOut, setCheckingOut] = useState(null); // planId currently submitting, or null
    const [checkoutError, setCheckoutError] = useState('');

    // Stripe redirects back to APP_URL?checkout=success|cancelled after
    // Checkout. The actual plan activation happens asynchronously via the
    // webhook (checkout.session.completed), not this redirect -- so
    // "success" here means "payment was submitted", not "your account is
    // updated yet". Cleared from the URL immediately so a page refresh
    // doesn't keep re-showing it.
    const [checkoutBanner] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const result = params.get('checkout');
        if (result) {
            window.history.replaceState({}, '', window.location.pathname);
        }
        return result;
    });

    const handleRequest = async () => {
        setSubmitting(true);
        setError('');
        try {
            await requestOrganizer();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit request');
        } finally {
            // Refresh top-level user state regardless of success or
            // failure -- if the request failed because a pending_review
            // row already existed (stale local state, e.g. from an
            // earlier session/tab), this self-corrects the UI to the
            // real backend state instead of leaving a contradictory
            // "submit a request" form next to an "already pending" error.
            onRequested && onRequested();
            setSubmitting(false);
        }
    };

    const PLANS = [
        { id: 'tier1', name: 'Starter',   price: '$29/yr',  blurb: '4 pools, 50 participants per pool' },
        { id: 'tier2', name: 'Growth',    price: '$49/yr',  blurb: '10 pools, 100 participants per pool' },
        { id: 'tier3', name: 'Unlimited', price: '$100/yr', blurb: 'Unlimited pools, 250 participants per pool' },
    ];

    const handleCheckout = async (planId) => {
        setCheckingOut(planId);
        setCheckoutError('');
        try {
            const res = await createCheckoutSession(planId);
            window.location.href = res.data.url;
            // No need to reset checkingOut on success -- the browser is
            // navigating away to Stripe Checkout.
        } catch (err) {
            setCheckoutError(err.response?.data?.error || 'Failed to start checkout');
            setCheckingOut(null);
        }
    };

    const banner = checkoutBanner === 'success' ? (
        <div className="max-w-lg mx-auto mb-6 bg-green-900/30 border border-green-700 rounded-xl p-4 text-center">
            <p className="text-green-400 text-sm font-bold">Payment received!</p>
            <p className="text-gray-400 text-xs mt-1">Your plan will activate in just a moment.</p>
        </div>
    ) : checkoutBanner === 'cancelled' ? (
        <div className="max-w-lg mx-auto mb-6 bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-gray-300 text-sm">Checkout was cancelled — no charge was made.</p>
        </div>
    ) : null;

    if (user.organizer_status === 'active' && !user.tos_accepted) {
        return (
            <div className="max-w-lg mx-auto text-center py-12">
                {banner}
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-6">
                    <p className="text-yellow-400 font-bold mb-1">Almost there — check your email</p>
                    <p className="text-gray-400 text-sm">
                        Your request has been approved! We've sent you an email with a link to
                        review and accept our Terms of Service and Privacy Policy. Once you accept,
                        you'll have full access to create tournaments and pools.
                    </p>
                </div>
            </div>
        );
    }

    if (user.organizer_status === 'active' && user.tos_accepted && !user.license_paid && user.plan_id !== 'eval') {
        return (
            <div className="max-w-2xl mx-auto py-12">
                {banner}
                <div className="text-center mb-8">
                    <p className="text-yellow-400 font-bold mb-1">Terms accepted — choose a plan</p>
                    <p className="text-gray-400 text-sm">
                        Pick an annual plan to activate your organizer account. You'll be redirected
                        to Stripe to complete payment securely.
                    </p>
                </div>
                {checkoutError && <p className="text-red-400 text-sm text-center mb-4">{checkoutError}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {PLANS.map(plan => (
                        <div key={plan.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center flex flex-col">
                            <h3 className="text-lg font-black text-white mb-1">{plan.name}</h3>
                            <p className="text-orange-400 font-bold mb-2">{plan.price}</p>
                            <p className="text-gray-500 text-sm mb-6 flex-grow">{plan.blurb}</p>
                            <button
                                onClick={() => handleCheckout(plan.id)}
                                disabled={checkingOut !== null}
                                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-4 py-2 rounded-lg transition-colors"
                            >
                                {checkingOut === plan.id ? 'Redirecting…' : 'Choose plan'}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (user.organizer_status === 'active') {
        return (
            <div className="max-w-lg mx-auto text-center py-12">
                {banner}
                <p className="text-gray-400">You're already an organizer.</p>
            </div>
        );
    }

    if (user.organizer_status === 'pending_review') {
        return (
            <div className="max-w-lg mx-auto text-center py-12">
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-6">
                    <p className="text-yellow-400 font-bold mb-1">Request pending review</p>
                    <p className="text-gray-400 text-sm">
                        Your request to become an organizer is awaiting approval.
                        You'll be able to create tournaments and pools once it's approved.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto py-12">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
                <h2 className="text-xl font-black text-white mb-2">Run your own bracket pool</h2>
                <p className="text-gray-500 text-sm mb-6">
                    {user.organizer_status === 'suspended'
                        ? "Your organizer access was previously suspended. You can request to have it reviewed again."
                        : "Request organizer access to create your own tournaments and pools. A platform admin will review your request, and you'll need to accept our Terms of Service and Privacy Policy before your account is fully active."}
                </p>
                {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
                <button onClick={handleRequest} disabled={submitting}
                    className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-6 py-3 rounded-lg transition-colors">
                    {submitting ? 'Submitting…' : 'Request to become an organizer'}
                </button>
            </div>
        </div>
    );
}

export default BecomeOrganizer;